import { createFileRoute } from "@tanstack/react-router";

import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HomeBody } from "@/components/home/HomeSections";
import { COMPANY } from "@/lib/company";
import {
  buildFaqSchema,
  buildServiceListSchema,
  buildHowToSchema,
  buildSpeakableSchema,
  buildAreasItemListSchema,
} from "@/lib/schema";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { getActiveProvince } from "@/provinces/_registry";
type HomeFaq = { q: string; a: string }[];

const OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/AA94fWlar0NrY1CKRIbY05Gk5II3/social-images/social-1779458272148-3.รูปภาพรวมทีมงานทนาย_Thai_Notary_Law_Image_6rzkaj6rzkaj6rzk.webp";

const HOME_SERVICES_FOR_SCHEMA = [
  { name: "Notary Public ภูเก็ต", description: "ทนายผู้ทำคำรับรองลายมือชื่อและเอกสารในจังหวัดภูเก็ต ขึ้นทะเบียนสภาทนายความฯ", priceFrom: 1500 },
  { name: "แปลเอกสาร NAATI / MoJ ในภูเก็ต", description: "บริการแปลเอกสารรับรอง NAATI สำหรับออสเตรเลีย/นิวซีแลนด์ และแปลรับรองกรมการกงสุล", priceFrom: 1300 },
  { name: "MFA / Apostille Legalization", description: "รับรองนิติกรณ์กรมการกงสุล (MFA) สำหรับเอกสารใช้ต่างประเทศ ส่งจากภูเก็ต", priceFrom: 2500 },
  { name: "รับรองสถานทูตและกงสุลกิตติมศักดิ์ในภูเก็ต", description: "ประสานกงสุลกิตติมศักดิ์ 22 ประเทศในเกาะภูเก็ต และสถานทูตในกรุงเทพ", priceFrom: 3500 },
  { name: "Police Clearance ตรวจประวัติอาชญากร", description: "ขอใบรับรองประวัติอาชญากรจากสำนักงานตำรวจแห่งชาติ", priceFrom: 4500 },
  { name: "LTR Visa Phuket (Long-Term Resident)", description: "วีซ่า 10 ปีสำหรับ Wealthy Pensioner / Global Citizen / Remote Worker / Highly Skilled ในภูเก็ต", priceFrom: 25000 },
  { name: "DTV Digital Nomad Visa Phuket", description: "Destination Thailand Visa สำหรับ Digital Nomad ที่ทำงานจากภูเก็ต พำนัก 180 วัน", priceFrom: 18000 },
  { name: "Retirement Visa O-A / O-X Phuket", description: "วีซ่าเกษียณอายุ 50+ ยื่นที่ ตม. ภูเก็ต", priceFrom: 15000 },
  { name: "Marriage Registration ภูเก็ต", description: "จดทะเบียนสมรสไทย-ต่างชาติที่ที่ว่าการอำเภอเมืองภูเก็ต/กะทู้/ถลาง", priceFrom: 12000 },
  { name: "Yacht / Marine Notary Phuket", description: "Seafarer Book, Yachtmaster License, Charter Agreement ที่อ่าวฉลอง Royal Phuket Marina, Boat Lagoon, Yacht Haven", priceFrom: 2500 },
  { name: "Property Conveyancing ภูเก็ต", description: "POA, Sale & Purchase, FET, โอนกรรมสิทธิ์คอนโด/วิลล่าที่สำนักงานที่ดินภูเก็ต", priceFrom: 25000 },
  { name: "BOI / Foreign Business License", description: "ตั้งบริษัทท่องเที่ยว โรงแรม ดำน้ำในภูเก็ต พร้อม Work Permit", priceFrom: 35000 },
];

export const Route = createFileRoute("/")({
  loader: async (): Promise<{
    areas: { slug: string; name: string; description?: string }[];
    faq: HomeFaq;
  }> => {
    const [{ PHUKET_AREA_PAGES }, { HOME_FAQ }] = await Promise.all([
      import("@/lib/phuket-area-pages"),
      import("@/content/home-faq"),
    ]);
    return {
      faq: HOME_FAQ,
      areas: PHUKET_AREA_PAGES.map((a) => ({
        slug: a.slug,
        name: `${a.nameTh} (${a.nameEn})`,
        description: a.intro?.slice(0, 140),
      })),
    };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const SITE_URL = getActiveSiteUrl();
    const HOME_TITLE = `Notary Public ${config.nameTh} | Thai Notary ${config.nameEn}`;
    const HOME_DESC = `Notary Public ${config.nameTh} ทนายสภาฯ รับรองเอกสาร แปล NAATI กงสุล MFA วีซ่า LTR/DTV/Retirement on-site ป่าตอง กะตะ ลากูน่า ฉลอง ☎ 094-895-8999`;
    const base = pageHeadForProvince({
      title: HOME_TITLE,
      description: HOME_DESC,
      path: "/",
      image: OG_IMAGE,
      imageAlt: `${COMPANY.brandName} ${config.nameEn} — ทีมทนาย Notary Public`,
      alternates: [{ lang: "en", path: "/en" }],
    });
    return ({
    meta: [
      ...base.meta,
      { name: "keywords", content: "notary public ภูเก็ต, ทนาย ภูเก็ต, รับรองเอกสาร ภูเก็ต, แปล NAATI ภูเก็ต, LTR Visa Phuket, DTV Digital Nomad Phuket, Yacht Notary Phuket, Honorary Consul Phuket, Notary Patong, Notary Laguna, Notary Chalong" },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" },
      { name: "googlebot", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      { name: "author", content: "Thai Notary Law & Service Co., Ltd." },
      // Geo signals
      { name: "geo.region", content: config.iso },
      { name: "geo.placename", content: `${config.nameEn}, Thailand` },
      { name: "geo.position", content: `${config.geo.lat};${config.geo.lng}` },
      { name: "ICBM", content: `${config.geo.lat}, ${config.geo.lng}` },
      { property: "og:locale", content: "th_TH" },
      { property: "og:locale:alternate", content: "en_US" },
    ],
    links: [
      ...base.links,
      // Note: hero is a CSS gradient with text (no image LCP candidate).
      // teamHero appears below the fold, so we lazy-load it instead of preloading.
      { rel: "dns-prefetch", href: "https://storage.googleapis.com" },
    ],
    scripts: [
      ...(loaderData?.faq?.length
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(buildFaqSchema(loaderData.faq)),
            },
          ]
        : []),
      { type: "application/ld+json", children: JSON.stringify(buildHowToSchema()) },
      { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/")) },
      ...buildServiceListSchema(HOME_SERVICES_FOR_SCHEMA).map((s) => ({
        type: "application/ld+json",
        children: JSON.stringify(s),
      })),
      ...(loaderData?.areas?.length
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(buildAreasItemListSchema(loaderData.areas)),
            },
          ]
        : []),
    ],
  });
  },
  component: Index,
});

function Index() {
  const { faq } = Route.useLoaderData();
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main id="main-content">
        <HomeBody faq={faq} />
      </main>
      <SiteFooter />
    </div>
  );
}
